import Button from '@/components/Button/Button';
import Modal from '@/components/Modal/Modal';
import useModalState from '@/hooks/pure/useModalState';
import useFeedbackSourceStore from '@/stores/Feedback/FeedbackSourceStore';
import useWorkspaceStore from '@/stores/WorkspaceStore';
import { SOURCE_TYPE } from '@/types/feedbackConst';

import IntegrationImportModal from '../../components/IntegrationImportModal';
import { ProviderContainer } from '../../components/ProviderContainer';
import type { IntegrationItemProps } from '../../types';
import type { FreshdeskInfo } from '../types';
import FreshdeskConnectModal from './FreshdeskConnectModal';
import { useFreshdesk } from './useFreshdesk';

export const FreshdeskItem = (props: IntegrationItemProps) => {
  const { isConnector } = props;
  const currWorkspace = useWorkspaceStore((state) => state.currWorkspace);
  const fetchFeedbackSources = useFeedbackSourceStore(
    (state) => state.fetchFeedbackSources,
  );

  const { freshdeskInfo, disconnectFreshdeskMutation, requestImportMutation } =
    useFreshdesk(!isConnector);

  const connectModalState = useModalState();
  const importModalState = useModalState<{
    info: FreshdeskInfo;
    isNew: boolean;
  }>();

  const handleConnectComplete = (info: FreshdeskInfo) => {
    if (!currWorkspace) return;
    fetchFeedbackSources(currWorkspace.alias);
    connectModalState.close();
    importModalState.open({
      modalState: {
        info,
        isNew: true,
      },
    });
  };

  const handleDisconnect = () => {
    disconnectFreshdeskMutation();
  };

  const handleImportWorkspace = async (dateRange: NullableDateRangeT) => {
    requestImportMutation?.(dateRange);
    importModalState.close();
  };

  return (
    <>
      <ProviderContainer
        multiInfoEnabled={false}
        connected={!!freshdeskInfo}
        source={SOURCE_TYPE.FRESHDESK}
        description="Import conversations as feedbacks into Syncly."
        connectedDisplay="Freshdesk workspace"
        info={freshdeskInfo!}
        onDisconnect={handleDisconnect}
        onConnectClick={() => connectModalState.open()}
        additionalActions={(info: FreshdeskInfo) => (
          <div>
            <Button
              variant="outlined"
              onClick={() =>
                importModalState.open({
                  modalState: {
                    info,
                    isNew: false,
                  },
                })
              }
            >
              One-time import
            </Button>
          </div>
        )}
      />
      <Modal
        status={connectModalState.status}
        closeModal={connectModalState.close}
      >
        <FreshdeskConnectModal onConnectComplete={handleConnectComplete} />
      </Modal>
      <Modal
        status={importModalState.status}
        closeModal={importModalState.close}
      >
        <IntegrationImportModal
          isNew={importModalState.modalState?.isNew || false}
          title={'Import feedback'}
          subTitle={'Freshdesk'}
          subTitleList={freshdeskInfo?.domain}
          historyList={freshdeskInfo?.history || []}
          onSubmitImport={(start_date, end_date) =>
            handleImportWorkspace({ start_date, end_date })
          }
        />
      </Modal>
    </>
  );
};
